import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  createContext,
  PropsWithChildren
} from "react";
import { socket } from "./socket";
import { orderBy } from "lodash-es";
import { message } from "antd";
import { ALERT } from "shared";
import { navigate } from "./router";

export type ALERT_CONTEXT = {
  alerts: ALERT[];
  resolve: (ids: string[]) => Promise<string[]>;
};

const context = createContext<ALERT_CONTEXT>({
  alerts: [],
  resolve: (_) => Promise.reject(),
});

export const AlertsProvider = ({ children }: PropsWithChildren) => {
  const [messageApi, contextHolder] = message.useMessage();
  const channel = useMemo(() => socket.channel("user_alerts"), []);

  const [alerts, setAlerts] = useState<ALERT[]>([]);

  useEffect(() => {
    socket.connect();
    channel.on("created", (alert: ALERT) => {
      setAlerts((alerts) => [alert, ...alerts]);
      const message = {
        type: alert.level,
        content: alert.message,
        onClick: () => navigate("/alerts"),
      };
      messageApi.open(message);
    });
    channel.on("resolved", ({ ids }: { ids: string[] }) => {
      setAlerts((alerts) => alerts.filter(({ id }) => !ids.includes(id)));
    });
    channel.join()
      .receive("ok", (alerts: ALERT[]) => {
        setAlerts(orderBy(alerts, ["timestamp"], ["desc"]));
      })
      .receive("error", (e) => console.error(e));
    
    return () => {
      channel.leave();
    };
  }, [channel, messageApi]);

  const resolve = useCallback((ids: string[]) => {
    return new Promise<string[]>((resolve, reject) => {
      channel.push("resolve", { ids })
        .receive("ok", ({ ids }: { ids: string[] }) => {
          resolve(ids);
        })
        .receive("error", (e) => {
          console.error(`error: ${JSON.stringify(e)}`);
          reject(e);
        });
    });
  }, [channel]);

  return <context.Provider value={{ alerts, resolve }}>
    {contextHolder}
    {children}
  </context.Provider>;
};

export const useAlerts = () => useContext(context);

